import React, { useEffect } from 'react';
import { Box, Typography, TextField, Button, IconButton, InputAdornment, Tooltip } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { milestoneFormStyles } from './MilestoneForm.styles';
import { DIVISION_SETTINGS_TEXTFIELD_LIMIT, INVALID_MILESTONE_NAME_EXISTS_IN_DIVISION, INVALID_MILESTONE_TASK_NAME_MATCH, INVALID_TASK_EXISTS_IN_MILESTONE } from '../../../../../../../../utils/constants';
import { MilestoneFormProps } from '../types';
import { useBrowserClosureWarning } from '../../../../../../../../utils/functions';

export const MilestoneForm: React.FC<MilestoneFormProps> = ({
     newMilestoneName,
     taskName,
     onMilestoneNameChange,
     onTaskNameChange,
     onSave,
     onCancel,
     existingTaskNames = [],
     existingMilestoneNames = [],
     currentMilestones = [],
     isExistingMilestone = false,
     isAddingMilestoneToForm,
     activateConfirmationDialog,
}) => {
     const milestoneNameTrim = newMilestoneName.trim();
     const taskNameTrim = taskName.trim();
     const namesMatch = !!milestoneNameTrim && !!taskNameTrim && milestoneNameTrim.toLowerCase() === taskNameTrim.toLowerCase();
     
     // Find which milestone contains the duplicate task
     const duplicateTaskMilestone = currentMilestones.find(
          milestone => milestone.taskName?.toLowerCase().trim() === taskNameTrim.toLowerCase()
     );
     const taskDuplicate = !!taskNameTrim && !!duplicateTaskMilestone;
     const duplicateMilestoneName = duplicateTaskMilestone?.mileStoneName || '';
     
     const isExistingName = !!milestoneNameTrim && existingMilestoneNames.includes(milestoneNameTrim.toLowerCase());
     const milestoneDuplicate = isAddingMilestoneToForm && !isExistingMilestone && isExistingName;
     const disableSave = !milestoneNameTrim || !taskNameTrim || namesMatch || taskDuplicate || milestoneDuplicate;

     /* Raise canSave state to parent (only when adding milestone form is visible) */
     useEffect(() => {
          if (activateConfirmationDialog) {
               activateConfirmationDialog(!disableSave);
          }
     }, [disableSave, activateConfirmationDialog]);

     /* Native browser prompt on tab close / refresh if there are unsaved changes. */
     useBrowserClosureWarning(!disableSave);

     return (
          <Box>
               <Box display="flex" sx={{ mb: 2 }}>
                    <Typography variant="h5" sx={milestoneFormStyles.title}>
                         Add Milestone Task
                    </Typography>
               </Box>
               <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                         fullWidth
                         value={newMilestoneName}
                         onChange={(e) => onMilestoneNameChange(e.target.value)}
                         variant="standard"
                         required
                         placeholder="Enter milestone name"
                         inputProps={{ maxLength: DIVISION_SETTINGS_TEXTFIELD_LIMIT }}
                         error={milestoneDuplicate}
                         helperText={
                              milestoneDuplicate
                                   ? INVALID_MILESTONE_NAME_EXISTS_IN_DIVISION
                                   : `${newMilestoneName.length}/${DIVISION_SETTINGS_TEXTFIELD_LIMIT} characters`
                         }
                         disabled={isExistingMilestone}
                         InputProps={{
                              endAdornment:
                                   !isExistingMilestone && milestoneNameTrim.length !== 0 ? (
                                        <InputAdornment position="end">
                                             <Tooltip title="Clear" arrow placement="top">
                                                  <IconButton
                                                       aria-label="Clear Milestone Name"
                                                       data-testid="clear-milestone-name"
                                                       size="small"
                                                       onClick={() => onMilestoneNameChange('')}
                                                  >
                                                       <ClearIcon style={{fontSize: "12px", stroke: "black", strokeWidth: 1.5}} />
                                                  </IconButton>
                                             </Tooltip>
                                        </InputAdornment>
                                   ) : null,
                         }}
                    />
                    <TextField
                         fullWidth
                         value={taskName}
                         onChange={(e) => onTaskNameChange(e.target.value)}
                         variant="standard"
                         required
                         placeholder="Enter task name"
                         error={taskDuplicate || namesMatch}
                         helperText={
                              taskDuplicate
                                   ? `${INVALID_TASK_EXISTS_IN_MILESTONE} in "${duplicateMilestoneName}"`
                                   : namesMatch
                                   ? INVALID_MILESTONE_TASK_NAME_MATCH
                                   : `${taskName.length}/${DIVISION_SETTINGS_TEXTFIELD_LIMIT} characters`
                         }
                         inputProps={{ maxLength: DIVISION_SETTINGS_TEXTFIELD_LIMIT }}
                         InputProps={{
                              endAdornment:
                                   taskNameTrim.length !== 0 ? (
                                        <InputAdornment position="end">
                                             <Tooltip title="Clear" arrow placement="top">
                                                  <IconButton
                                                       aria-label="Clear Task Name"
                                                       data-testid="clear-task-name"
                                                       size="small"
                                                       onClick={() => onTaskNameChange('')}
                                                  >
                                                       <ClearIcon style={{fontSize: "12px", stroke: "black", strokeWidth: 1.5}} />
                                                  </IconButton>
                                             </Tooltip>
                                        </InputAdornment>
                                   ) : null,
                         }}
                    />
               </Box>
               <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                         onClick={() => {
                              onCancel();
                              onMilestoneNameChange('');
                              onTaskNameChange('');
                         }}
                         style={{ textTransform: 'none' }}
                    >
                         Cancel
                    </Button>
                    <Button
                         variant="contained"
                         onClick={onSave}
                         style={{ textTransform: 'none' }}
                         disabled={disableSave}
                    >
                         Save
                    </Button>
               </Box>
          </Box>
     );
};